import React, { useEffect, useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Filter,
  RefreshCw
} from 'lucide-react';
import { API_BASE_URL } from '../services/api';

const REVIEW_ROLES = ['admin', 'state', 'dist-ac', 'dist-ddo', 'dist-bdo', 'zonal'];

const ReportsPage = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDays, setSelectedDays] = useState(15);
  const [selectedReport, setSelectedReport] = useState('same-stage');
  const [selectedScheme, setSelectedScheme] = useState('all');
  const [userRole, setUserRole] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedDesignation, setSelectedDesignation] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [userOptions, setUserOptions] = useState([]);

  // Drill-down report state.
  const [expandedBlock, setExpandedBlock] = useState('');
  const [expandedSummaryKey, setExpandedSummaryKey] = useState('');
  const [officialRows, setOfficialRows] = useState({});
  const [expandedOfficialKey, setExpandedOfficialKey] = useState('');
  const [panchayatRows, setPanchayatRows] = useState({});
  const [drillLoading, setDrillLoading] = useState('');

  const daysOptions = [
    { value: 15, label: '15 Days' },
    { value: 30, label: '30 Days' },
    { value: 60, label: '60 Days' },
    { value: 90, label: '90 Days' }
  ];

  const reportOptions = [
    { value: 'same-stage', label: 'Same Stage Report', endpoint: '/project/report/same-stage', schemeFilter: true },
    { value: 'higher-area', label: 'Variation in Size Report', endpoint: '/project/report/higher-area', schemeFilter: true },
    {
  value: 'block-status',
  label: 'Block-wise Status Report',
  endpoint: '/user/home',
  schemeFilter: true,
  adminOnly: true
},
    { value: 'inspection-visits', label: 'Inspection Visit Report', endpoint: '/project/list/inspection', schemeFilter: true, periodFilter: true },
    { value: 'inspection-summary', label: 'Block / Designation Inspection Summary', endpoint: '/project/report/inspection-summary', periodFilter: true },
    { value: 'draft-inspections', label: 'Draft Inspection Report', endpoint: '/project/report/draft-inspections', periodFilter: true, reviewOnly: true }
  ];

  const currentReport = reportOptions.find((item) => item.value === selectedReport);
  const schemeFilterEnabled = !!currentReport?.schemeFilter;
  const periodFilterEnabled = !!currentReport?.periodFilter;

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        const user = JSON.parse(userData);
        setUserRole(user.role || '');
      } catch (err) {
        console.error('Error parsing user data:', err);
      }
    }
  }, []);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/user`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        if (response.ok) setUserOptions(data.data || []);
      } catch (err) {
        // Some higher-level report users may not be allowed to list all users.
        // The new drill-down report does not depend on this list.
        console.error('Failed to load report users:', err);
      }
    };

    fetchUsers();
  }, []);

  const designationOptions = [...new Set(
    userOptions.map((user) => user.designation || user.role).filter(Boolean)
  )].sort();

  const getAvailableReports = () => reportOptions.filter((option) => {
    if (option.adminOnly && userRole !== 'admin') return false;
    if (option.reviewOnly && !REVIEW_ROLES.includes(userRole)) return false;
    return true;
  });

 const getAvailableSchemes = () => {
  if (selectedReport === 'block-status') {
    const source = reportData?.scheme_stage_status || {};

    return [...new Set(
      Object.values(source).flatMap((blockData) =>
        Object.keys(blockData || {})
      )
    )].sort();
  }

  if (!Array.isArray(reportData)) return [];

  return [...new Set(
    reportData.map((item) => item.scheme).filter(Boolean)
  )].sort();
};

  const getFilteredReportData = () => {
    if (!Array.isArray(reportData)) return reportData;
    if (!schemeFilterEnabled || selectedScheme === 'all') return reportData;
    return reportData.filter((item) => item.scheme === selectedScheme);
  };

  const getInspectionVisitSummary = () => {
  const rows = getFilteredReportData();

  if (!Array.isArray(rows)) return [];

  const officerMap = new Map();

  rows.forEach((item) => {
    const key =
      item.inspecting_user_id ||
      `${item.inspecting_official_name || 'Unknown'}_${item.inspecting_official_contact || ''}`;

    if (!officerMap.has(key)) {
      officerMap.set(key, {
        official_id: item.inspecting_user_id || key,
        official_name: item.inspecting_official_name || 'Unknown',
        designation: item.inspecting_designation || 'N/A',
        contact: item.inspecting_official_contact || '',
        inspection_count: 0
      });
    }

    officerMap.get(key).inspection_count += 1;
  });

  return Array.from(officerMap.values()).sort(
    (a, b) => b.inspection_count - a.inspection_count
  );
};

const getSameStageSummary = () => {
  const rows = getFilteredReportData();

  if (!Array.isArray(rows)) return [];

  const summary = new Map();

  rows.forEach((item) => {
    const block = item.block || 'Unknown';
    const scheme = item.scheme || 'Unknown Scheme';
    const stage = item.stage || 'Not Started';
    const key = `${block}__${scheme}__${stage}`;

    if (!summary.has(key)) {
      summary.set(key, {
        block,
        scheme,
        stage,
        count: 0
      });
    }

    summary.get(key).count += 1;
  });

  return Array.from(summary.values()).sort((a, b) =>
    a.block.localeCompare(b.block) ||
    a.scheme.localeCompare(b.scheme) ||
    a.stage.localeCompare(b.stage)
  );
};

const getAreaRangeSummary = () => {
  const rows = getFilteredReportData();

  if (!Array.isArray(rows)) return [];

  const rangeMap = new Map();

  rows.forEach((item) => {
    const actualArea = parseFloat(
      String(item.ins_house_size || '').replace(/[^\d.]/g, '')
    );

    if (!Number.isFinite(actualArea) || actualArea < 0) return;

    const start = Math.floor(actualArea / 100) * 100;
    const end = start + 100;
    const key = `${start}-${end}`;

    if (!rangeMap.has(key)) {
      rangeMap.set(key, {
        start,
        range: `${start} - ${end} sq ft`,
        count: 0
      });
    }

    rangeMap.get(key).count += 1;
  });

  return Array.from(rangeMap.values()).sort(
    (a, b) => a.start - b.start
  );
};

const formatDesignation = (designation) => {
  if (designation === 'pncyt-bdo') return 'Zonal Deputy BDO';
  return designation || '-';
};

const formatArea = (value) => {
  if (value === null || value === undefined || value === '') return 'N/A';

  const text = String(value).trim();

  return /sq\s*ft/i.test(text)
    ? text
    : `${text} sq ft`;
};

  const buildPeriodParams = () => {
    const params = new URLSearchParams();
    if (fromDate) params.set('from', fromDate);
    if (toDate) params.set('to', toDate);
    return params;
  };

  const fetchReportData = async () => {
    const selectedOption = reportOptions.find((item) => item.value === selectedReport);
    if (!selectedOption) return;

    setLoading(true);
    setError('');
    setReportData(selectedReport === 'block-status' ? {} : []);
    setExpandedBlock('');
    setExpandedSummaryKey('');
    setExpandedOfficialKey('');
    setOfficialRows({});
    setPanchayatRows({});

    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}${selectedOption.endpoint}`;
      const params = new URLSearchParams();

      if (selectedReport === 'same-stage') {
        params.set('days', selectedDays);
        if (selectedScheme !== 'all') params.set('scheme', selectedScheme);
      } else if (selectedReport === 'higher-area') {
        if (selectedScheme !== 'all') params.set('scheme', selectedScheme);
      } else if (selectedReport === 'inspection-visits') {
        if (fromDate) params.set('start', fromDate);
        if (toDate) params.set('end', toDate);
        if (selectedScheme !== 'all') params.set('scheme', selectedScheme);
        if (selectedDesignation) params.set('designation', selectedDesignation);
        if (selectedUser) params.set('user_id', selectedUser);
      } else if (selectedReport === 'inspection-summary' || selectedReport === 'draft-inspections') {
        if (fromDate) params.set('from', fromDate);
        if (toDate) params.set('to', toDate);
      }

      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (!response.ok) {
        setError(data.message || 'Failed to fetch report data');
        return;
      }

      setReportData(selectedReport === 'block-status' ? (data.data || {}) : (data.data || []));
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedScheme('all');
    setSelectedDesignation('');
    setSelectedUser('');
    fetchReportData();
    // selectedReport is intentionally the trigger here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  useEffect(() => {
    if (selectedReport === 'same-stage') fetchReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays]);

  const fetchOfficials = async (row) => {
    const key = `${row.block}__${row.designation}`;
    if (expandedSummaryKey === key) {
      setExpandedSummaryKey('');
      setExpandedOfficialKey('');
      return;
    }

    setExpandedSummaryKey(key);
    setExpandedOfficialKey('');
    if (officialRows[key]) return;

    setDrillLoading(`official:${key}`);
    try {
      const token = localStorage.getItem('token');
      const params = buildPeriodParams();
      params.set('block', row.block);
      params.set('designation', row.designation);

      const endpoint = selectedReport === 'draft-inspections'
        ? '/project/report/draft-officials'
        : '/project/report/inspection-officials';
      const response = await fetch(
        `${API_BASE_URL}${endpoint}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to load officials');
      setOfficialRows((prev) => ({ ...prev, [key]: data.data || [] }));
    } catch (err) {
      setError(err.message || 'Failed to load official drill-down');
    } finally {
      setDrillLoading('');
    }
  };

  const fetchPanchayats = async (summaryRow, official) => {
    const summaryKey = `${summaryRow.block}__${summaryRow.designation}`;
    const officialKey = `${summaryKey}__${official.official_id}`;

    if (expandedOfficialKey === officialKey) {
      setExpandedOfficialKey('');
      return;
    }

    setExpandedOfficialKey(officialKey);
    if (panchayatRows[officialKey]) return;

    setDrillLoading(`panchayat:${officialKey}`);
    try {
      const token = localStorage.getItem('token');
      const params = buildPeriodParams();
      params.set('block', summaryRow.block);
      params.set('official_id', official.official_id);

      const endpoint = selectedReport === 'draft-inspections'
        ? '/project/report/draft-panchayats'
        : '/project/report/inspection-panchayats';
      const response = await fetch(
        `${API_BASE_URL}${endpoint}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to load panchayats');
      setPanchayatRows((prev) => ({ ...prev, [officialKey]: data.data || [] }));
    } catch (err) {
      setError(err.message || 'Failed to load panchayat drill-down');
    } finally {
      setDrillLoading('');
    }
  };

  const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const downloadCSV = () => {
    if (selectedReport === 'block-status') {
      downloadBlockStatusCSV();
      return;
    }

    const dataToDownload = getFilteredReportData();
    if (!Array.isArray(dataToDownload) || dataToDownload.length === 0) {
      alert('No data to download');
      return;
    }

    let headers = [];
    let csvData = [];

    if (selectedReport === 'same-stage') {
      headers = ['Housing ID', 'Beneficiary Name', 'District', 'Block', 'Panchayat', 'Scheme', 'Current Stage'];
      csvData = dataToDownload.map((item) => [
        item.h_id, item.beneficiary, item.district, item.block, item.panchayat, item.scheme, item.stage
      ]);
    } else if (selectedReport === 'higher-area') {
      headers = ['Housing ID', 'Beneficiary Name', 'District', 'Block', 'Panchayat', 'Scheme', 'Planned Area (sq ft)', 'Actual Area (sq ft)'];
      csvData = dataToDownload.map((item) => [
        item.h_id, item.beneficiary, item.district, item.block, item.panchayat,
        item.scheme, item.scheme_house_size, item.ins_house_size
      ]);
      } else if (selectedReport === 'inspection-visits') {
      headers = [
        'S.No',
        'Inspecting Official',
        'Designation',
        'Official Contact',
        'No. of Inspections'
      ];

      csvData = getInspectionVisitSummary().map((officer, index) => [
        index + 1,
        officer.official_name,
        officer.designation,
        officer.contact,
        officer.inspection_count
      ]);
    } else if (selectedReport === 'inspection-summary') {
      headers = [
        'Block',
        'Designation',
        'Official',
        'Contact',
        'Panchayat',
        'Total Inspections',
        'Unique Houses'
      ];

      csvData = dataToDownload.map((item) => [
        item.block,
        formatDesignation(item.designation),
        item.official_name,
        item.contact,
        item.panchayat,
        item.total_inspections,
        item.unique_houses
      ]);
    } else if (selectedReport === 'draft-inspections') {
      headers = ['Level', 'Block', 'Designation', 'Draft Inspections', 'Unique Houses', 'Oldest Draft', 'Latest Draft'];
      csvData = dataToDownload.flatMap((item) => [
        ['Block', item.block, '', item.draft_count, item.unique_houses, item.oldest_draft_date, item.latest_draft_date],
        ...(item.designations || []).map((designation) => [
          'Designation', item.block, designation.designation, designation.draft_count,
          designation.unique_houses, designation.oldest_draft_date, designation.latest_draft_date
        ])
      ]);
    }

    const csvContent = [headers, ...csvData]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const reportName = currentReport?.label.toLowerCase().replace(/\s+/g, '_') || 'report';
    link.setAttribute('download', `${reportName}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getBlockStageRows = () => {
  const source = reportData?.scheme_stage_status || {};

  return Object.keys(source)
    .sort()
    .map((block) => {
      const blockSchemes = source[block] || {};

      const selectedSchemeMaps =
        selectedScheme === 'all'
          ? Object.values(blockSchemes)
          : [blockSchemes[selectedScheme] || {}];

      const stages = {};

      selectedSchemeMaps.forEach((stageMap) => {
        Object.entries(stageMap || {}).forEach(([stage, count]) => {
          stages[stage] =
            (stages[stage] || 0) + Number(count || 0);
        });
      });

      const total = Object.values(stages).reduce(
        (sum, count) => sum + Number(count || 0),
        0
      );

      const completed = Number(stages.Completed || 0);
      const pending = total - completed;

      const completionPercentage =
        total > 0
          ? ((completed / total) * 100).toFixed(2)
          : '0.00';

      return {
        block,
        stages,
        total,
        completed,
        pending,
        completionPercentage
      };
    })
    .filter((row) => row.total > 0);
};

const getBlockStageColumns = () => {
  const rows = getBlockStageRows();

  const stageSet = new Set();

  rows.forEach((row) => {
    Object.keys(row.stages || {}).forEach((stage) => {
      // Completed is already shown as summary column.
      if (stage !== 'Completed') {
        stageSet.add(stage);
      }
    });
  });

  return Array.from(stageSet).sort();
};

  const downloadBlockStatusCSV = () => {
  const rows = getBlockStageRows();
  const stages = getBlockStageColumns();

  if (!rows.length) {
    alert('No data to download');
    return;
  }

  const headers = [
    'Block Name',
    'Total Houses',
    'Completed',
    'Pending',
    '% Completion',
    ...stages
  ];

  const csvData = rows.map((row) => [
    row.block,
    row.total,
    row.completed,
    row.pending,
    row.completionPercentage,
    ...stages.map((stage) => row.stages?.[stage] || 0)
  ]);

  const csvContent = [headers, ...csvData]
    .map((row) => row.map(escapeCsv).join(','))
    .join('\n');

  const blob = new Blob(
    [csvContent],
    { type: 'text/csv;charset=utf-8;' }
  );

  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.href = url;
  link.download =
    `block_stage_breakup_${selectedScheme === 'all' ? 'all_schemes' : selectedScheme}_${new Date()
      .toISOString()
      .split('T')[0]}.csv`;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const renderBlockStatusTable = () => {
  const rows = getBlockStageRows();
  const stages = getBlockStageColumns();

  if (!rows.length) return null;

  return (
    <table className="w-full text-sm whitespace-nowrap">
      <thead className="bg-blue-50">
        <tr>
          <th className="px-3 py-3 text-left">Block Name</th>
          <th className="px-3 py-3 text-center">Total Houses</th>
          <th className="px-3 py-3 text-center">Completed</th>
          <th className="px-3 py-3 text-center">Pending</th>
          <th className="px-3 py-3 text-center">% Completion</th>

          {stages.map((stage) => (
            <th
              key={stage}
              className="px-3 py-3 text-center"
            >
              {stage}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {rows.map((row) => (
          <tr
            key={row.block}
            className="border-t hover:bg-gray-50"
          >
            <td className="px-3 py-3 font-medium">
              {row.block}
            </td>

            <td className="px-3 py-3 text-center font-semibold">
              {row.total}
            </td>

            <td className="px-3 py-3 text-center text-green-700 font-semibold">
              {row.completed}
            </td>

            <td className="px-3 py-3 text-center text-orange-700 font-semibold">
              {row.pending}
            </td>

            <td className="px-3 py-3 text-center">
              {row.completionPercentage}%
            </td>

            {stages.map((stage) => (
              <td
                key={stage}
                className="px-3 py-3 text-center"
              >
                {row.stages?.[stage] || 0}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

  const renderInspectionSummarySinglePage = () => {
  const rows = Array.isArray(reportData) ? reportData : [];

  return (
    <table className="w-full text-sm">
      <thead className="bg-blue-50">
        <tr>
          <th className="px-4 py-3 text-left">Block</th>
          <th className="px-4 py-3 text-left">Designation</th>
          <th className="px-4 py-3 text-left">Official</th>
          <th className="px-4 py-3 text-left">Contact</th>
          <th className="px-4 py-3 text-left">Panchayat</th>
          <th className="px-4 py-3 text-center">Total Inspections</th>
          <th className="px-4 py-3 text-center">Unique Houses</th>
        </tr>
      </thead>

      <tbody>
        {rows.map((row, index) => (
          <tr
            key={`${row.block}-${row.designation}-${row.official_id}-${row.panchayat}-${index}`}
            className="border-t hover:bg-gray-50"
          >
            <td className="px-4 py-3 font-medium">
              {row.block || '-'}
            </td>

            <td className="px-4 py-3">
              {formatDesignation(row.designation)}
            </td>

            <td className="px-4 py-3">
              {row.official_name || '-'}
            </td>

            <td className="px-4 py-3">
              {row.contact || '-'}
            </td>

            <td className="px-4 py-3">
              {row.panchayat || '-'}
            </td>

            <td className="px-4 py-3 text-center font-semibold">
              {row.total_inspections || 0}
            </td>

            <td className="px-4 py-3 text-center font-semibold text-blue-700">
              {row.unique_houses || 0}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};


  const renderInspectionSummary = (draftMode = false) => {
    const blocks = Array.isArray(reportData) ? reportData : [];
    const totalKey = draftMode ? 'draft_count' : 'total_inspections';
    const totalLabel = draftMode ? 'Draft Inspections' : 'Total Inspections';
    const accentRow = draftMode ? 'hover:bg-amber-50' : 'hover:bg-blue-50';
    const expandedBg = draftMode ? 'bg-amber-50/40' : 'bg-blue-50/40';

    return (
      <table className="w-full text-sm">
        <thead className={draftMode ? 'bg-amber-50' : 'bg-gray-50'}>
          <tr>
            <th className="px-4 py-3 text-left">Block</th>
            <th className="px-4 py-3 text-center">{totalLabel}</th>
            <th className="px-4 py-3 text-center">Unique Houses</th>
          </tr>
        </thead>
        <tbody>
          {blocks.map((blockRow) => {
            const blockExpanded = expandedBlock === blockRow.block;
            const designations = blockRow.designations || [];

            return (
              <React.Fragment key={blockRow.block}>
                <tr
                  className={`border-t cursor-pointer ${accentRow}`}
                  onClick={() => {
                    setExpandedBlock(blockExpanded ? '' : blockRow.block);
                    setExpandedSummaryKey('');
                    setExpandedOfficialKey('');
                  }}
                >
                  <td className="px-4 py-3 font-semibold">
                    <span className="inline-flex items-center gap-2">
                      {blockExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      {blockRow.block}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-semibold">{blockRow[totalKey] || 0}</td>
                  <td className="px-4 py-3 text-center font-semibold text-blue-700">{blockRow.unique_houses || 0}</td>
                </tr>

                {blockExpanded && (
                  <tr className={expandedBg}>
                    <td colSpan="3" className="px-8 py-4">
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">
                        Designation-wise {draftMode ? 'draft inspection' : 'inspection'} count
                      </div>
                      <table className="w-full bg-white border rounded-lg overflow-hidden">
                        <thead className={draftMode ? 'bg-amber-50' : 'bg-blue-50'}>
                          <tr>
                            <th className="px-4 py-2 text-left">Designation</th>
                            <th className="px-4 py-2 text-center">{totalLabel}</th>
                            <th className="px-4 py-2 text-center">Unique Houses</th>
                          </tr>
                        </thead>
                        <tbody>
                          {designations.map((designationRow) => {
                            const key = `${blockRow.block}__${designationRow.designation}`;
                            const designationExpanded = expandedSummaryKey === key;
                            const officials = officialRows[key] || [];

                            return (
                              <React.Fragment key={key}>
                                <tr
                                  className="border-t hover:bg-gray-50 cursor-pointer"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    fetchOfficials({ ...designationRow, block: blockRow.block });
                                  }}
                                >
                                  <td className="px-4 py-2 font-medium">
                                    <span className="inline-flex items-center gap-2">
                                      {designationExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                      {designationRow.designation}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2 text-center">{designationRow[totalKey] || 0}</td>
                                  <td className="px-4 py-2 text-center text-blue-700">{designationRow.unique_houses || 0}</td>
                                </tr>

                                {designationExpanded && (
                                  <tr className="bg-gray-50">
                                    <td colSpan="3" className="px-8 py-3">
                                      {drillLoading === `official:${key}` ? (
                                        <div className="text-sm text-gray-600">Loading officials...</div>
                                      ) : officials.length === 0 ? (
                                        <div className="text-sm text-gray-500">No official details found.</div>
                                      ) : (
                                        <table className="w-full bg-white border">
                                          <thead className="bg-gray-100">
                                            <tr>
                                              <th className="px-4 py-2 text-left">Official</th>
                                              <th className="px-4 py-2 text-left">Contact</th>
                                              <th className="px-4 py-2 text-center">{draftMode ? 'Drafts' : 'Total'}</th>
                                              <th className="px-4 py-2 text-center">Unique</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {officials.map((official) => {
                                              const officialKey = `${key}__${official.official_id}`;
                                              const officialExpanded = expandedOfficialKey === officialKey;
                                              const panchayats = panchayatRows[officialKey] || [];

                                              return (
                                                <React.Fragment key={officialKey}>
                                                  <tr
                                                    className="border-t hover:bg-white cursor-pointer"
                                                    onClick={(event) => {
                                                      event.stopPropagation();
                                                      fetchPanchayats(
                                                        { ...designationRow, block: blockRow.block },
                                                        official
                                                      );
                                                    }}
                                                  >
                                                    <td className="px-4 py-2 font-medium">
                                                      <span className="inline-flex items-center gap-2">
                                                        {officialExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                        {official.official_name}
                                                      </span>
                                                    </td>
                                                    <td className="px-4 py-2">{official.contact || '-'}</td>
                                                    <td className="px-4 py-2 text-center">{official[totalKey] || 0}</td>
                                                    <td className="px-4 py-2 text-center text-blue-700">{official.unique_houses || 0}</td>
                                                  </tr>

                                                  {officialExpanded && (
                                                    <tr className="bg-slate-50">
                                                      <td colSpan="4" className="px-8 py-3">
                                                        {drillLoading === `panchayat:${officialKey}` ? (
                                                          <div className="text-sm text-gray-600">Loading panchayats...</div>
                                                        ) : panchayats.length === 0 ? (
                                                          <div className="text-sm text-gray-500">No panchayat details found.</div>
                                                        ) : (
                                                          <table className="w-full bg-white border">
                                                            <thead className="bg-slate-100">
                                                              <tr>
                                                                <th className="px-4 py-2 text-left">Panchayat</th>
                                                                <th className="px-4 py-2 text-center">{totalLabel}</th>
                                                                <th className="px-4 py-2 text-center">Unique Houses</th>
                                                              </tr>
                                                            </thead>
                                                            <tbody>
                                                              {panchayats.map((panchayat) => (
                                                                <tr key={panchayat.panchayat} className="border-t">
                                                                  <td className="px-4 py-2">{panchayat.panchayat}</td>
                                                                  <td className="px-4 py-2 text-center">{panchayat[totalKey] || 0}</td>
                                                                  <td className="px-4 py-2 text-center text-blue-700">{panchayat.unique_houses || 0}</td>
                                                                </tr>
                                                              ))}
                                                            </tbody>
                                                          </table>
                                                        )}
                                                      </td>
                                                    </tr>
                                                  )}
                                                </React.Fragment>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      )}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderReportTable = () => {
    if (selectedReport === 'block-status') return renderBlockStatusTable();
    //if (selectedReport === 'inspection-summary') return renderInspectionSummary(false);
    if (selectedReport === 'inspection-summary') return renderInspectionSummarySinglePage();
    if (selectedReport === 'draft-inspections') return renderInspectionSummary(true);

    const tableData = getFilteredReportData();

   if (selectedReport === 'same-stage') {
  const summaryData = getSameStageSummary();

  return (
    <div>
      <div className="p-4 border-b">
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          Same Stage Abstract
        </h3>

        <table className="w-full text-sm border">
          <thead className="bg-blue-50">
            <tr>
              <th className="px-4 py-3 text-left">Block</th>
              <th className="px-4 py-3 text-left">Scheme</th>
              <th className="px-4 py-3 text-left">Stage</th>
              <th className="px-4 py-3 text-center">No. of Houses</th>
            </tr>
          </thead>
          <tbody>
            {summaryData.map((item, index) => (
              <tr
                key={`${item.block}-${item.scheme}-${item.stage}-${index}`}
                className="border-t hover:bg-gray-50"
              >
                <td className="px-4 py-3 font-medium">{item.block}</td>
                <td className="px-4 py-3">{item.scheme}</td>
                <td className="px-4 py-3">{item.stage}</td>
                <td className="px-4 py-3 text-center font-bold text-blue-700">
                  {item.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          Detailed Report
        </h3>

        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">Housing Details</th>
              <th className="px-6 py-3 text-left">Location</th>
              <th className="px-6 py-3 text-left">Current Stage</th>
            </tr>
          </thead>
          <tbody>
            {tableData.map((item, index) => (
              <tr key={item.id || index} className="border-t hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="font-medium">{item.h_id}</div>
                  <div className="text-sm text-gray-600">{item.beneficiary}</div>
                  <div className="text-xs text-gray-500">{item.mobile}</div>
                </td>

                <td className="px-6 py-4">
                  <div>{item.district}</div>
                  <div className="text-sm text-gray-600">{item.block}</div>
                  <div className="text-xs text-gray-500">{item.panchayat}</div>
                </td>

                <td className="px-6 py-4">
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    {item.stage}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">
                    {item.scheme}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

  if (selectedReport === 'higher-area') {
  const areaSummary = getAreaRangeSummary();

  return (
    <div>
      <div className="p-4 border-b">
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          Variation in Size Abstract
        </h3>

        <table className="w-full text-sm border">
          <thead className="bg-blue-50">
            <tr>
              <th className="px-4 py-3 text-left">Actual Size Range</th>
              <th className="px-4 py-3 text-center">No. of Houses</th>
            </tr>
          </thead>

          <tbody>
            {areaSummary.map((item) => (
              <tr key={item.range} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3 font-medium">
                  {item.range}
                </td>
                <td className="px-4 py-3 text-center font-bold text-blue-700">
                  {item.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-3">
          Detailed Report
        </h3>

        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">Housing Details</th>
              <th className="px-6 py-3 text-left">Location</th>
              <th className="px-6 py-3 text-left">Area Details</th>
            </tr>
          </thead>

          <tbody>
            {tableData.map((item, index) => (
              <tr key={item.id || index} className="border-t hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="font-medium">{item.h_id}</div>
                  <div className="text-sm text-gray-600">
                    {item.beneficiary}
                  </div>
                </td>

                <td className="px-6 py-4">
                  <div>{item.block}</div>
                  <div className="text-sm text-gray-600">
                    {item.panchayat}
                  </div>
                </td>

                <td className="px-6 py-4">
                  <div>
                    Allotted: {formatArea(item.scheme_house_size)}
                  </div>
                  <div>
                    Actual: {formatArea(item.ins_house_size)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

    if (selectedReport === 'inspection-visits') {
  const officerSummary = getInspectionVisitSummary();

  return (
    <table className="w-full">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-4 py-3 text-left">S.No</th>
          <th className="px-4 py-3 text-left">Inspecting Official</th>
          <th className="px-4 py-3 text-left">Designation</th>
          <th className="px-4 py-3 text-left">Contact</th>
          <th className="px-4 py-3 text-center">No. of Inspections</th>
        </tr>
      </thead>

      <tbody>
        {officerSummary.map((officer, index) => (
          <tr
            key={officer.official_id}
            className="border-t hover:bg-gray-50"
          >
            <td className="px-4 py-3">
              {index + 1}
            </td>

            <td className="px-4 py-3 font-medium">
              {officer.official_name}
            </td>

            <td className="px-4 py-3">
              {officer.designation}
            </td>

            <td className="px-4 py-3">
              {officer.contact || '-'}
            </td>

            <td className="px-4 py-3 text-center font-bold text-blue-700">
              {officer.inspection_count}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

    return null;
  };

  const getRecordCount = () => {
    if (selectedReport === 'block-status') {
  const rows = getBlockStageRows();
  return rows.length ? `${rows.length} blocks` : '';
}
    if (selectedReport === 'inspection-visits') {
  const officers = getInspectionVisitSummary();
  return officers.length ? `${officers.length} officers` : '';
}

  const rows = getFilteredReportData();
  return Array.isArray(rows) && rows.length ? `${rows.length} records` : '';
    };

  const hasData = selectedReport === 'block-status'
  ? getBlockStageRows().length > 0
  : Array.isArray(getFilteredReportData()) &&
    getFilteredReportData().length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex flex-col gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <FileText className="w-6 h-6 text-blue-600" />
                  Reports
                </h1>
                <p className="text-gray-600">Generate, drill down and download inspection reports</p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <select
                  value={selectedReport}
                  onChange={(event) => setSelectedReport(event.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                >
                  {getAvailableReports().map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>

                {schemeFilterEnabled && (
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <select
                      value={selectedScheme}
                      onChange={(event) => setSelectedScheme(event.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                    >
                      <option value="all">All Schemes</option>
                      {getAvailableSchemes().map((scheme) => <option key={scheme} value={scheme}>{scheme}</option>)}
                    </select>
                  </div>
                )}

                {selectedReport === 'same-stage' && (
                  <select
                    value={selectedDays}
                    onChange={(event) => setSelectedDays(Number(event.target.value))}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                  >
                    {daysOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                  </select>
                )}

                {periodFilterEnabled && (
                  <>
                    <label className="text-sm text-gray-600">From</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(event) => setFromDate(event.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                    />
                    <label className="text-sm text-gray-600">To</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(event) => setToDate(event.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                    />
                  </>
                )}

                {selectedReport === 'inspection-visits' && (
                  <>
                    <select
                      value={selectedDesignation}
                      onChange={(event) => {
                        setSelectedDesignation(event.target.value);
                        setSelectedUser('');
                      }}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                    >
                      <option value="">All Designations</option>
                      {designationOptions.map((designation) => <option key={designation} value={designation}>{designation}</option>)}
                    </select>
                    <select
                      value={selectedUser}
                      onChange={(event) => setSelectedUser(event.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md"
                    >
                      <option value="">All Users</option>
                      {userOptions
                        .filter((user) => !selectedDesignation || (user.designation || user.role) === selectedDesignation)
                        .map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}
                    </select>
                  </>
                )}

                <button
                  onClick={fetchReportData}
                  disabled={loading}
                  className="inline-flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Apply / Refresh
                </button>

                <button
                  onClick={downloadCSV}
                  disabled={loading || !hasData}
                  className="inline-flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              {error}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {currentReport?.label || 'Report'}
                {selectedReport === 'same-stage' && ` (${selectedDays}+ Days)`}
                {getRecordCount() && <span className="ml-2 text-sm font-normal text-gray-600">({getRecordCount()})</span>}
              </h2>
              {selectedReport === 'draft-inspections' && (
                <p className="text-sm text-gray-500 mt-1">
                  Click a block to see designation-wise counts, then drill down to officials and Panchayat-wise totals / unique houses.
                </p>
              )}
            </div>

            {loading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
                <p className="text-gray-600">Loading report data...</p>
              </div>
            ) : hasData ? (
              <div className="overflow-x-auto">{renderReportTable()}</div>
            ) : (
              <div className="p-8 text-center">
                <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-2">No data found</p>
                <p className="text-sm text-gray-500">No records found for the selected criteria</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
